'use client';

import React, { createContext, use, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { MATTER_ROUTES } from '@/lib/constants';

interface CreateMatterContextType {
  /**
   * Backwards-compatible. The create-matter and intake-first flows are now
   * full-page routes under `/matters/new` rather than sheets, so this is always
   * `false`. Retained only for the legacy (unmounted) create-matter sheet.
   */
  isOpen: boolean;
  /**
   * Navigate to the full-page create-matter flow, skipping the chooser and
   * starting directly in the AI create flow (`/matters/new?start=create`).
   */
  open: () => void;
  /**
   * Navigate to the full-page entry at the "How would you like to get started?"
   * chooser (`/matters/new`). From there the user picks the AI create flow or
   * the intake-first flow, both of which advance in-page.
   */
  openIntermediary: () => void;
}

const CreateMatterContext = createContext<CreateMatterContextType | undefined>(
  undefined
);

export function CreateMatterProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();

  // The create-matter entry is a full page (`/matters/new`). The chooser is the
  // first step of that page; `open()` skips it (straight to the AI create flow)
  // while `openIntermediary()` lands on it. The intake-first flow is reached
  // from the chooser and advances in-page — no sheet, so no context flow state.
  const open = useCallback(() => {
    router.push(`${MATTER_ROUTES.NEW}?start=create`);
  }, [router]);
  const openIntermediary = useCallback(() => {
    router.push(MATTER_ROUTES.NEW);
  }, [router]);

  const isOpen = false;

  const value = useMemo<CreateMatterContextType>(
    () => ({ isOpen, open, openIntermediary }),
    [isOpen, open, openIntermediary]
  );

  return <CreateMatterContext value={value}>{children}</CreateMatterContext>;
}

export function useCreateMatter() {
  const context = use(CreateMatterContext);
  if (context === undefined) {
    throw new Error(
      'useCreateMatter must be used within a CreateMatterProvider'
    );
  }
  return context;
}
